import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { ApiError, type EmployeeSummary } from '../api/client.ts';
import { GrantsPanel } from '../features/access/GrantsPanel.tsx';

const STATUS_LABEL = { invited: 'Invited', active: 'Active', deactivated: 'Deactivated' } as const;

/** Organization employees: invite by email, deactivate, manage grants (requires `employees.manage`). */
export function UsersPage() {
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/users' });
  const queryClient = useQueryClient();
  const employees = useQuery({ queryKey: ['employees'], queryFn: () => api.listEmployees() });
  const [email, setEmail] = useState('');
  const [selected, setSelected] = useState<EmployeeSummary | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['employees'] });
  const invite = useMutation({
    mutationFn: (value: string) => api.invite(value),
    onSuccess: () => (setEmail(''), refresh()),
  });
  const deactivate = useMutation({ mutationFn: (id: string) => api.deactivate(id), onSuccess: refresh });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim()) invite.mutate(email.trim());
  };
  const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

  return (
    <section>
      <h1>Users</h1>
      <form className="inline-form" onSubmit={submit}>
        <label>
          <span className="visually-hidden">Email to invite</span>
          <input
            type="email"
            aria-label="Email to invite"
            placeholder="name@company.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>
        <button type="submit" disabled={invite.isPending}>
          Invite
        </button>
        {invite.isError && <span role="alert">{errorText(invite.error)}</span>}
      </form>
      {employees.isPending ? (
        <p aria-busy="true">Loading users…</p>
      ) : employees.isError ? (
        <p role="alert">Users could not be loaded.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Name</th>
              <th scope="col">Status</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {employees.data.map((employee) => (
              <tr key={employee.id}>
                <td>{employee.email}</td>
                <td>{employee.name ?? '—'}</td>
                <td>{STATUS_LABEL[employee.status]}</td>
                <td className="actions">
                  <button type="button" onClick={() => setSelected(employee)} aria-label={`Grants for ${employee.email}`}>
                    Grants
                  </button>
                  {employee.status !== 'deactivated' && (
                    <button
                      type="button"
                      onClick={() => deactivate.mutate(employee.id)}
                      disabled={deactivate.isPending}
                    >
                      Deactivate
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {deactivate.isError && <p role="alert">{errorText(deactivate.error)}</p>}
      {selected && <GrantsPanel key={selected.id} employee={selected} />}
    </section>
  );
}
